import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Grid, Box, Card } from "@material-ui/core";

import {
  tableCellRender,
  formatDateValue,
  dateFilterOperators,
  stringFilterOperators,
  isSingleSelectOperators,
  getLatestUpdateDaily,
  getCompanyCodeName,
  getCompanyCodeFilter,
  filterModelToQueryValue,
} from "../util/tableUtils";
import { DataGridCystellarTable } from "../util/DataGridCystellarTable";
import { UpdateMapButton } from "../util/UpdateMapButton";
import { goToClaimPage } from "./utils";
import httpClient from "../../lib/httpClient";
import { UniversalMap } from "../map/UniversalMap";
import { saveUserMonitorData } from "../util/userUtils";
import { useClientConfig } from "../../context/clientConfigContext";

export const ClaimsList = () => {
  const { t } = useTranslation();
  const config = useClientConfig();

  const [claimsData, setClaimsData] = useState([]);
  const [tableState, setTableState] = useState({
    isLoading: false,
    total: 0,
  });

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [filterModel, setFilterModel] = useState({});
  const [sortModel, setSortModel] = useState({});
  const [filteredMapData, setFilteredMapData] = useState(null);
  const [lastUpdateDateMsg, setLastUpdateDateMsg] = useState("");

  const getClaimsData = useCallback(async () => {
    setTableState((old) => ({ ...old, isLoading: true }));
    let sortField, sortDirection;

    const sortItem = sortModel;
    if (sortItem && Object.keys(sortItem).length) {
      const { field, sort } = sortItem[0];
      sortField = field;
      sortDirection = sort;
    }

    try {
      const { data: claimsResponse } = await httpClient().get(
        `/claimsdb?page=${paginationModel.page}&pageSize=${
          paginationModel.pageSize
        }&filters=${filterModelToQueryValue(
          filterModel,
        )}&orderBy=${sortField}&orderDirection=${sortDirection}`,
      );
      setClaimsData(claimsResponse.data);
      setTableState((old) => ({
        ...old,
        isLoading: false,
        total: claimsResponse.total,
      }));
    } catch (err) {
      console.log(err);
    }
  }, [filterModel, sortModel, paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    getLatestUpdateDaily("/serviceLogs/buildings?service=db_claims_daily").then((lastUpdateMsg) => {
      setLastUpdateDateMsg(lastUpdateMsg);
    });
    saveUserMonitorData("claimsList", "");
  }, []);

  useEffect(() => {
    if (filterModel) {
      const isAnyIncompleteFilter = filterModel?.items?.some((filter) => !filter?.value);

      if (!isAnyIncompleteFilter) {
        getClaimsData();
      }
    } else {
      getClaimsData();
    }
  }, [filterModel, getClaimsData]);

  const onFilterChange = useCallback((aFilterModel) => {
    setFilterModel(aFilterModel);
  }, []);

  const onSortChange = useCallback((aSortModel) => {
    setSortModel(aSortModel);
  }, []);

  const claimsColumns = [
    {
      field: "companyCode" || "company",
      headerName: t("company"),
      width: 100,
      type: "singleSelect",
      filterOperators: isSingleSelectOperators,
      valueOptions: getCompanyCodeFilter(config.companyCodesMap),
      valueGetter: (params) => getCompanyCodeName(params.row.companyCode, config.companyCodesMap),
    },
    {
      field: "claimCode",
      headerName: t("claims.claimCode"),
      sortable: false,
    },
    {
      field: "policyNumberSecondary",
      headerName: t("policy.id"),
      sortable: false,
    },
    {
      field: "referenceDate",
      headerName: t("coverage.year"),
    },
    {
      field: "claimAddressCity",
      headerName: t("city"),
    },
    {
      field: "claimAddressCityCode",
      headerName: t("cityCode"),
      sortable: false,
    },
    {
      field: "claimAddressStreetName",
      headerName: t("address"),
      valueGetter: (params) =>
        `${params.row.claimAddressType ? params.row.claimAddressType : ""} ${
          params.row.claimAddressStreetName ? params.row.claimAddressStreetName : ""
        } ${params.row.claimAddressBuildingNo ? params.row.claimAddressBuildingNo : ""}`,
    },
    {
      field: "claimAddressZIPCode",
      headerName: t("zip"),
      sortable: false,
    },
    {
      field: "claimAddressProvince",
      headerName: t("state"),
    },
    {
      field: "claimReportDate",
      headerName: t("claims.reportDate"),
      type: "date",
      sortable: false,
      filterOperators: dateFilterOperators,
      valueGetter: (params) =>
        params.row.claimReportDate ? formatDateValue(params.row.claimReportDate) : "",
    },
    {
      field: "claimEventDate",
      headerName: t("claims.claimEventDate"),
      type: "date",
      sortable: false,
      filterOperators: dateFilterOperators,
      valueGetter: (params) => (params.row.claimEventDate ? formatDateValue(params.row.claimEventDate) : ""),
    },
    {
      field: "agency",
      headerName: t("agency"),
      valueGetter: (params) => `${params.row.agencyCode} ${params.row.agencyName}`,
    },
    {
      field: "claimCauseDescription",
      headerName: t("claims.claimCause"),
    },
    {
      field: "claimLiquidationCentreDescription",
      headerName: t("claims.liquidationCenter"),
    },
    {
      field: "productCommercial",
      headerName: t("product"),
      sortable: false,
      valueGetter: (params) => params.row?.productCommercial,
    },
    ...(config.tableColumns.coinsuranceType
      ? [
          {
            field: "coinsuranceType",
            headerName: t("coinsuranceType"),
            valueGetter: (params) => params.row?.coinsuranceType,
            type: "singleSelect",
            sortable: false,
            filterOperators: isSingleSelectOperators,
            valueOptions: [
              { value: "01 - NO COASSI", label: "01 - NO COASSI" },
              {
                value: "02 - COASSI DELEGA NOSTRA",
                label: "02 - COASSI DELEGA NOSTRA",
              },
            ],
          },
        ]
      : []),
    {
      field: "perilName",
      headerName: t("perilName"),
    },
    {
      field: "perilGroup",
      headerName: t("perilGroup.title"),
    },
  ];

  const columns = claimsColumns.map((tableColumn) => ({
    valueFormatter: (params) => tableCellRender(params.value?.toString()),
    filterOperators: stringFilterOperators,
    flex: 1,
    ...tableColumn,
  }));

  return (
    <Grid style={{ marginTop: 25 }}>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={12}>
          <Box>
            <Card>
              <UniversalMap
                page="buildings-claims"
                filteredMapDataProp={filteredMapData}
                setFilteredMapDataProp={setFilteredMapData}
                routeNameProp="claims"
              />
            </Card>
          </Box>
        </Grid>
        <Grid item xs={12} sm={12}>
          <div style={{ height: 700, width: "100%" }}>
            <DataGridCystellarTable
              columns={columns}
              rows={claimsData}
              rowCount={tableState.total}
              getRowId={(rowData) => rowData._id}
              onRowClick={(rowData) => goToClaimPage(rowData.row)}
              loading={tableState.isLoading}
              paginationMode={"server"}
              paginationModel={paginationModel}
              onPaginationModelChange={(newPaginationModel) => setPaginationModel(newPaginationModel)}
              filterMode={"server"}
              onFilterModelChange={onFilterChange}
              sortingMode={"server"}
              onSortModelChange={onSortChange}
              customToolbarComp={lastUpdateDateMsg ? true : false}
              customToolbarCompMsg={lastUpdateDateMsg}
              customToolbarWidgets={
                <UpdateMapButton
                  setFilteredMapData={setFilteredMapData}
                  filterModel={filterModel}
                  routeName="claims"
                />
              }
            />
          </div>
        </Grid>
      </Grid>
    </Grid>
  );
};
